/**
 * The pieces every Owner page is built from.
 *
 * Eight components, no styling decisions of their own beyond a class name --
 * layout belongs to `styles/owner.css`, materials to the shared Rumonai skin.
 * They exist so that four
 * pages cannot quietly grow four different card headers and four ideas about
 * where a timestamp goes.
 *
 * One of them carries a rule rather than a shape: `Pill` states its meaning in
 * a word as well as a colour. Colour alone is not a status for a reader who
 * cannot see it.
 */

import type { ReactNode } from "react";
import { Icon, type IconName } from "./Icon";

export type Tone = "neutral" | "good" | "waiting" | "bad";

/** The page's own header: a small kicker, the name, and one sentence. */
export function PageHead({
  kicker,
  title,
  lead,
  aside,
}: {
  kicker: string;
  title: string;
  /** One sentence about what is on this page right now, not about the page. */
  lead: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <header className="own-head">
      <div className="own-head-copy">
        <p className="own-kicker">{kicker}</p>
        <h1 className="own-title" tabIndex={-1}>
          {title}
        </h1>
        <p className="own-lead">{lead}</p>
      </div>
      {aside ? <div className="own-head-aside">{aside}</div> : null}
    </header>
  );
}

/**
 * The one thing this page wants the owner to do, above everything else.
 *
 * At most one per page. A second band is a page that has not decided what it is
 * for.
 */
export function Band({
  icon,
  title,
  lead,
  action,
  tone = "neutral",
}: {
  icon: IconName;
  title: string;
  lead: string;
  action?: ReactNode;
  tone?: Tone;
}) {
  return (
    <section className="own-band pa-bounded-surface" data-tone={tone}>
      <span className="own-band-mark" aria-hidden="true">
        <Icon name={icon} size={20} />
      </span>
      <div className="own-band-copy">
        <p className="own-band-title">{title}</p>
        <p className="own-band-lead">{lead}</p>
      </div>
      {action ? <div className="own-band-action">{action}</div> : null}
    </section>
  );
}

export function Section({
  icon,
  title,
  lead,
  count,
  action,
  children,
}: {
  icon?: IconName;
  title: string;
  /** What this section is, in the owner's terms. Always present. */
  lead: string;
  /** Drawn beside the title. Omit when a count is not a fact here. */
  count?: number;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="own-section">
      <header className="own-section-head">
        <h2 className="own-section-title">
          {icon ? <Icon name={icon} size={17} /> : null}
          {title}
          {count === undefined ? null : <span className="own-count num">{count}</span>}
        </h2>
        <p className="own-section-lead">{lead}</p>
        {action ? <div className="own-section-action">{action}</div> : null}
      </header>
      {children}
    </section>
  );
}

/**
 * One line of a card's body.
 *
 * `quoted` says the value is somebody else's words -- a person's message, a
 * sentence the model wrote -- shown as they wrote it. The vocabulary gate
 * (`scripts/owner-vocab.mjs`) skips these, because the rule it enforces is
 * about what this shell puts on the page: quoting an owner who typed a tool
 * name back at them is the page telling the truth, and editing it would be the
 * page lying. Labels are never quoted -- those are ours.
 */
export type LineRow = { label: string; value: ReactNode; quoted?: boolean };

/** A stack of cards. One column: these are read in order, not scanned. */
export function Cards({ children }: { children: ReactNode }) {
  return <div className="own-cards">{children}</div>;
}

export function Card({
  className = "",
  tone = "neutral",
  title,
  quotedTitle = false,
  when,
  badge,
  children,
  actions,
}: {
  className?: string;
  tone?: Tone;
  title: ReactNode;
  /** The title is somebody's own words, shown as they said them. */
  quotedTitle?: boolean;
  /** Already in words (`whenWords`); never a raw timestamp. */
  when?: string;
  badge?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <article className={`own-card ${actions ? "pa-card" : "pa-open-group"} ${className}`.trim()} data-tone={tone}>
      <header className="own-card-head">
        <h3 className="own-card-title" data-quoted={quotedTitle ? "true" : undefined}>
          {title}
        </h3>
        <div className="own-card-side">
          {badge}
          {when ? <time className="own-when">{when}</time> : null}
        </div>
      </header>
      {children ? <div className="own-card-body">{children}</div> : null}
      {actions ? <footer className="own-card-actions">{actions}</footer> : null}
    </article>
  );
}

/** A status, said in a word and shown in a colour. Never colour alone. */
export function Pill({ tone = "neutral", children }: { tone?: Tone; children: ReactNode }) {
  return (
    <span className="own-pill" data-tone={tone}>
      <span className="own-dot" aria-hidden="true" />
      {children}
    </span>
  );
}

/** The details, folded away. Open it and it is still sentences. */
export function Details({ label, children }: { label: string; children: ReactNode }) {
  return (
    <details className="own-details">
      <summary className="own-details-summary">
        <Icon name="down" size={14} />
        {label}
      </summary>
      <div className="own-details-body">{children}</div>
    </details>
  );
}

/** A label and a value, one per line, both in words. */
export function Lines({ rows }: { rows: LineRow[] }) {
  return (
    <dl className="own-lines">
      {rows.map((row, i) => (
        <div key={`${row.label}-${i}`}>
          <dt>{row.label}</dt>
          <dd data-quoted={row.quoted ? "true" : undefined}>{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}
